import { BrowserAuthOptions, LogLevel, ProtocolMode, Configuration } from "@azure/msal-browser";
import { AxiosInstance } from "axios";

/**
 * Fetches and initializes MSAL configuration from the backend and returns the MSAL config object and scope.
 * @param axiosInstance Axios instance to use for the request
 * @param serviceUrl Service URL to fetch config from
 * @returns {Promise<{ msalConfig: Configuration, scope: string }>} The MSAL config and scope
 */
export async function InitMsalConfig(axiosInstance: AxiosInstance, serviceUrl: string) {
  // Fetch MSAL config from service endpoint
  const response = await axiosInstance.get(`https://${serviceUrl}/api/auth/config`);
  const config = response.data;
  const scope = config.scope;
  let redirectUri = location.protocol + "//" + location.hostname + (location.port ? ":" + location.port : "") + "/fallbackauthdialog.html";
  let authConf: BrowserAuthOptions;
  if (config.provider === "AAD") {
    authConf = {
      clientId: config.clientId,
      authority: `https://login.microsoftonline.com/${config.tenant}`,
      redirectUri: redirectUri,
      navigateToLoginRequestUrl: false,
    };
  } else if (config.provider === "AADB2C") {
    authConf = {
      clientId: config.clientId,
      authority: `https://${config.tenantName}.b2clogin.com/${config.tenantName}.onmicrosoft.com/${config.signUpSignInPolicyId}`,
      redirectUri: redirectUri,
      knownAuthorities: [`${config.tenantName}.b2clogin.com`],
      navigateToLoginRequestUrl: false,
    };
  } else if (config.provider === "OKTA") {
    authConf = {
      clientId: config.clientId,
      authority: `https://${config.tenant}.okta.com/oauth2/default`,
      redirectUri: redirectUri,
      cloudDiscoveryMetadata: JSON.stringify({
        "tenant_discovery_endpoint": `https://${config.tenant}.okta.com/oauth2/default/.well-known/openid-configuration`,
        "metadata": [
          {
            "preferred_network": `${config.tenant}.okta.com`,
            "preferred_cache": `${config.tenant}.okta.com`,
            "aliases": [`${config.tenant}.okta.com`]
          }
        ]
      }),
      postLogoutRedirectUri: window.location.origin + '/logout',
      navigateToLoginRequestUrl: false,
      protocolMode: ProtocolMode.OIDC
    };
  } else {
    authConf = {
      clientId: "",
      authority: "",
      redirectUri: redirectUri,
      navigateToLoginRequestUrl: false,
    };
  }
  // Build MSAL config object
  const msalConfig: Configuration = {
    auth: authConf,
    cache: {
      cacheLocation: "localStorage",
    },
    system: {
      loggerOptions: {
        logLevel: process.env.NODE_ENV === "development" ? LogLevel.Verbose : LogLevel.Warning,
        loggerCallback: (level: LogLevel, message: string, containsPii: boolean): void => {
          if (containsPii) {
            return;
          }
          switch (level) {
            case LogLevel.Verbose:
              console.debug(message);
              return;
            case LogLevel.Info:
              console.info(message);
              return;
            case LogLevel.Warning:
              console.warn(message);
              return;
            case LogLevel.Error:
              console.error(message);
              return;
          }
        },
        piiLoggingEnabled: false,
      },
      loadFrameTimeout: 600000,
      tokenRenewalOffsetSeconds: 300,
    },
  };
  return { msalConfig, scope };
}
